import {
  discoveredCount,
  isAssigned,
  photographedCount,
  type QuestState,
} from './questState'
import type { Quest, QuestGoal, QuestStep, TraversalId } from './types'

export interface StepProgress {
  stepId: string
  text: string
  done: boolean
  have: number
  need: number
}

export interface QuestProgress {
  questId: string
  complete: boolean
  steps: StepProgress[]
  done: number
  total: number
}

function goalSatisfied(id: string, goal: QuestGoal, state: QuestState): boolean {
  const record = state.discoveries[id]
  if (!record) return false
  if (goal.type === 'photograph') return Boolean(record.photographedAt)
  return true
}

function evaluateGoal(goal: QuestGoal, state: QuestState): { have: number; need: number } {
  if (goal.ids?.length) {
    const matched = goal.ids.filter((id) => goalSatisfied(id, goal, state)).length
    if (goal.count != null) {
      return { have: Math.min(matched, goal.count), need: goal.count }
    }
    return { have: matched, need: goal.ids.length }
  }
  if (goal.count != null) {
    const total =
      goal.type === 'photograph' ? photographedCount(state) : discoveredCount(state)
    return { have: Math.min(total, goal.count), need: goal.count }
  }
  return { have: 0, need: 1 }
}

function evaluateStep(step: QuestStep, state: QuestState): StepProgress {
  const { have, need } = evaluateGoal(step.goal, state)
  return {
    stepId: step.id,
    text: step.text,
    done: have >= need,
    have,
    need,
  }
}

export function evaluateQuest(quest: Quest, state: QuestState): QuestProgress {
  const steps = quest.steps.map((step) => evaluateStep(step, state))
  const done = steps.filter((step) => step.done).length
  return {
    questId: quest.id,
    complete: steps.length > 0 && done === steps.length,
    steps,
    done,
    total: steps.length,
  }
}

export function evaluateAll(quests: Quest[], state: QuestState): QuestProgress[] {
  return quests.map((quest) => evaluateQuest(quest, state))
}

/** Traversals granted by every quest whose steps are all satisfied. */
export function unlockedTraversals(quests: Quest[], state: QuestState): TraversalId[] {
  const unlocked: TraversalId[] = []
  for (const quest of quests) {
    if (quest.reward.type !== 'traversal') continue
    if (!evaluateQuest(quest, state).complete) continue
    // Opening quest must be assigned by Don Rafa before the toucan unlocks.
    if (quest.order === 1 && !isAssigned(state, quest.id)) continue
    unlocked.push(quest.reward.value as TraversalId)
  }
  return unlocked
}

export function isTraversalUnlocked(
  quests: Quest[],
  state: QuestState,
  id: TraversalId,
): boolean {
  return unlockedTraversals(quests, state).includes(id)
}

/** Quests complete now but not yet in state.announced. */
export function newlyCompleted(quests: Quest[], state: QuestState): Quest[] {
  const announced = new Set(state.announced ?? [])
  return quests.filter(
    (quest) =>
      evaluateQuest(quest, state).complete &&
      !announced.has(quest.id) &&
      (quest.order !== 1 || isAssigned(state, quest.id)),
  )
}
